/**
 * WhatsApp WABA State Store.
 * Manages multi-WABA mappings, catalogs, and products.
 */

import { create } from 'zustand';
import type {
  WhatsAppCatalog,
  WhatsAppProduct,
  WhatsAppCallSettings,
  WhatsAppActiveCall,
  WhatsAppCallStatus,
} from '@/modules/tpa/whatsapp/whatsapp-types';
import type { WhatsAppCallHandler } from '@/modules/tpa/whatsapp/whatsapp-call-handler';

interface WhatsAppStore {
  // Multi-WABA mappings
  availableWabaIds: string[];
  numbersVsWabaIdMap: Record<string, string>;
  wabaIdVsNumbersMap: Record<string, string[]>;
  numberVsBusinessIdMap: Record<string, string>;
  whatsappNumberVsIdMap: Record<string, string>;
  senderIdVsNumberMap: Record<string, string>;

  // Catalogs & Products
  wabaCatalogs: Record<string, WhatsAppCatalog>;
  wabaProducts: Record<string, WhatsAppProduct>;
  wabaIdVsCatalogId: Record<string, string>;
  catalogFetchedWabaIds: string[];
  catalogFetchedBusinessIds: string[];

  // Calling
  callSettings: Record<string, WhatsAppCallSettings>;
  callingEnabled: Record<string, boolean>;
  activeWhatsAppCall: WhatsAppActiveCall | null;
  callHandler: WhatsAppCallHandler | null;
  callDurationTimer: ReturnType<typeof setInterval> | null;

  // Actions — mapping management
  setAvailableWabaIds(ids: string[]): void;
  registerSender(
    phoneNumber: string,
    senderId: string,
    wabaId: string,
    businessId?: string
  ): void;

  // Actions — catalog management
  addCatalog(wabaId: string, catalog: WhatsAppCatalog): void;
  addProducts(products: WhatsAppProduct[]): void;
  markCatalogFetched(wabaId: string): void;
  markBusinessCatalogFetched(businessId: string): void;
  isCatalogFetched(wabaId: string): boolean;
  isBusinessCatalogFetched(businessId: string): boolean;

  // Lookups
  getWabaIdForNumber(msisdn: string): string | null;
  getWabaIdForSenderId(senderId: string): string | null;
  getBusinessIdForNumber(msisdn: string): string | null;
  getBusinessIdForSenderId(senderId: string): string | null;
  getCatalogIdForWabaId(wabaId: string): string | null;
  getCatalogIdForSenderId(senderId: string): string | null;
  getMSISDNFromSenderId(senderId: string): string | null;
  getSenderIdFromMSISDN(msisdn: string): string | null;
  getProductByRetailerId(retailerId: string): WhatsAppProduct | null;

  // Actions — calling
  setCallSettings(phoneNumberId: string, settings: WhatsAppCallSettings): void;
  setCallingEnabled(phoneNumberId: string, enabled: boolean): void;
  setActiveCall(call: WhatsAppActiveCall): void;
  updateCallStatus(status: WhatsAppCallStatus | 'connecting' | 'idle'): void;
  setCallMuted(isMuted: boolean): void;
  clearActiveCall(): void;
  setCallHandler(handler: WhatsAppCallHandler | null): void;
  startCallDurationTimer(): void;
  stopCallDurationTimer(): void;
  isCallingEnabledForSender(phoneNumberId: string): boolean;

  reset(): void;
}

export const useWhatsAppStore = create<WhatsAppStore>((set, get) => ({
  availableWabaIds: [],
  numbersVsWabaIdMap: {},
  wabaIdVsNumbersMap: {},
  numberVsBusinessIdMap: {},
  whatsappNumberVsIdMap: {},
  senderIdVsNumberMap: {},
  wabaCatalogs: {},
  wabaProducts: {},
  wabaIdVsCatalogId: {},
  catalogFetchedWabaIds: [],
  catalogFetchedBusinessIds: [],
  callSettings: {},
  callingEnabled: {},
  activeWhatsAppCall: null,
  callHandler: null,
  callDurationTimer: null,

  setAvailableWabaIds: (ids) => set({ availableWabaIds: ids }),

  registerSender: (phoneNumber, senderId, wabaId, businessId) => {
    const state = get();
    const msisdn = phoneNumber.replace(/\D/g, '');

    const numbersVsWabaIdMap = { ...state.numbersVsWabaIdMap, [msisdn]: wabaId };
    const whatsappNumberVsIdMap = { ...state.whatsappNumberVsIdMap, [msisdn]: senderId };
    const senderIdVsNumberMap = { ...state.senderIdVsNumberMap, [senderId]: msisdn };

    const wabaIdVsNumbersMap = { ...state.wabaIdVsNumbersMap };
    if (!wabaIdVsNumbersMap[wabaId]) {
      wabaIdVsNumbersMap[wabaId] = [];
    }
    if (!wabaIdVsNumbersMap[wabaId].includes(msisdn)) {
      wabaIdVsNumbersMap[wabaId] = [...wabaIdVsNumbersMap[wabaId], msisdn];
    }

    const numberVsBusinessIdMap = businessId
      ? { ...state.numberVsBusinessIdMap, [msisdn]: businessId }
      : state.numberVsBusinessIdMap;

    set({
      numbersVsWabaIdMap,
      wabaIdVsNumbersMap,
      whatsappNumberVsIdMap,
      senderIdVsNumberMap,
      numberVsBusinessIdMap,
    });
  },

  addCatalog: (wabaId, catalog) => {
    const state = get();
    const wabaCatalogs = { ...state.wabaCatalogs, [catalog.id]: catalog };
    const wabaIdVsCatalogId = { ...state.wabaIdVsCatalogId, [wabaId]: catalog.id };

    // Index products by retailer_id
    const wabaProducts = { ...state.wabaProducts };
    if (catalog.products?.data) {
      for (const prod of catalog.products.data) {
        wabaProducts[prod.retailer_id] = prod;
      }
    }

    set({ wabaCatalogs, wabaIdVsCatalogId, wabaProducts });
  },

  addProducts: (products) => {
    const wabaProducts = { ...get().wabaProducts };
    for (const prod of products) {
      wabaProducts[prod.retailer_id] = prod;
    }
    set({ wabaProducts });
  },

  markCatalogFetched: (wabaId) => {
    const ids = get().catalogFetchedWabaIds;
    if (!ids.includes(wabaId)) {
      set({ catalogFetchedWabaIds: [...ids, wabaId] });
    }
  },

  markBusinessCatalogFetched: (businessId) => {
    const ids = get().catalogFetchedBusinessIds;
    if (!ids.includes(businessId)) {
      set({ catalogFetchedBusinessIds: [...ids, businessId] });
    }
  },

  isCatalogFetched: (wabaId) => get().catalogFetchedWabaIds.includes(wabaId),

  isBusinessCatalogFetched: (businessId) =>
    get().catalogFetchedBusinessIds.includes(businessId),

  // ── Lookups ──

  getWabaIdForNumber: (msisdn) => {
    const clean = msisdn.replace(/\D/g, '');
    return get().numbersVsWabaIdMap[clean] ?? null;
  },

  getWabaIdForSenderId: (senderId) => {
    const msisdn = get().senderIdVsNumberMap[senderId];
    if (!msisdn) return null;
    return get().numbersVsWabaIdMap[msisdn] ?? null;
  },

  getBusinessIdForNumber: (msisdn) => {
    const clean = msisdn.replace(/\D/g, '');
    return get().numberVsBusinessIdMap[clean] ?? null;
  },

  getBusinessIdForSenderId: (senderId) => {
    const msisdn = get().senderIdVsNumberMap[senderId];
    if (!msisdn) return null;
    return get().numberVsBusinessIdMap[msisdn] ?? null;
  },

  getCatalogIdForWabaId: (wabaId) => get().wabaIdVsCatalogId[wabaId] ?? null,

  getCatalogIdForSenderId: (senderId) => {
    const wabaId = get().getWabaIdForSenderId(senderId);
    if (!wabaId) return null;
    return get().wabaIdVsCatalogId[wabaId] ?? null;
  },

  getMSISDNFromSenderId: (senderId) => get().senderIdVsNumberMap[senderId] ?? null,

  getSenderIdFromMSISDN: (msisdn) => {
    const clean = msisdn.replace(/\D/g, '');
    return get().whatsappNumberVsIdMap[clean] ?? null;
  },

  getProductByRetailerId: (retailerId) => get().wabaProducts[retailerId] ?? null,

  // ── Calling ──

  setCallSettings: (phoneNumberId, settings) => {
    set((s) => ({
      callSettings: { ...s.callSettings, [phoneNumberId]: settings },
    }));
  },

  setCallingEnabled: (phoneNumberId, enabled) => {
    set((s) => ({
      callingEnabled: { ...s.callingEnabled, [phoneNumberId]: enabled },
    }));
  },

  setActiveCall: (call) => set({ activeWhatsAppCall: call }),

  updateCallStatus: (status) => {
    const current = get().activeWhatsAppCall;
    if (!current) return;
    set({ activeWhatsAppCall: { ...current, status } });
  },

  setCallMuted: (isMuted) => {
    const current = get().activeWhatsAppCall;
    if (!current) return;
    set({ activeWhatsAppCall: { ...current, isMuted } });
  },

  clearActiveCall: () => {
    get().stopCallDurationTimer();
    const handler = get().callHandler;
    if (handler) {
      handler.destroy();
    }
    set({ activeWhatsAppCall: null, callHandler: null });
  },

  setCallHandler: (handler) => set({ callHandler: handler }),

  startCallDurationTimer: () => {
    get().stopCallDurationTimer();
    const timer = setInterval(() => {
      const call = get().activeWhatsAppCall;
      if (call) {
        set({ activeWhatsAppCall: { ...call, duration: call.duration + 1 } });
      }
    }, 1000);
    set({ callDurationTimer: timer });
  },

  stopCallDurationTimer: () => {
    const timer = get().callDurationTimer;
    if (timer) {
      clearInterval(timer);
      set({ callDurationTimer: null });
    }
  },

  isCallingEnabledForSender: (phoneNumberId) => {
    return get().callingEnabled[phoneNumberId] ?? false;
  },

  reset: () => {
    get().stopCallDurationTimer();
    const handler = get().callHandler;
    if (handler) handler.destroy();
    set({
      availableWabaIds: [],
      numbersVsWabaIdMap: {},
      wabaIdVsNumbersMap: {},
      numberVsBusinessIdMap: {},
      whatsappNumberVsIdMap: {},
      senderIdVsNumberMap: {},
      wabaCatalogs: {},
      wabaProducts: {},
      wabaIdVsCatalogId: {},
      catalogFetchedWabaIds: [],
      catalogFetchedBusinessIds: [],
      callSettings: {},
      callingEnabled: {},
      activeWhatsAppCall: null,
      callHandler: null,
      callDurationTimer: null,
    });
  },
}));
